import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  Description as DocumentIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import AddEmployee from './AddEmployee';
import EditEmployee from './EditEmployee';

// Sample data
const businessOptions = [
  { id: 1, name: 'Acme Corp' },
  { id: 2, name: 'Globex Inc' },
];

const branchOptions = [
  { id: 1, name: 'New York', businessId: 1 },
  { id: 2, name: 'San Francisco', businessId: 1 },
  { id: 3, name: 'London', businessId: 2 },
];

const departmentOptions = [
  'Human Resources',
  'Information Technology',
  'Finance',
  'Marketing',
  'Sales',
  'Operations',
];

const designationOptions = [
  'Software Engineer',
  'Senior Software Engineer',
  'Team Lead',
  'Project Manager',
  'HR Manager',
  'Finance Manager',
];

const initialEmployees = [
  {
    id: 1,
    firstName: 'John',
    lastName: 'Doe',
    name: 'John Doe',
    code: 'EMP001',
    email: 'john@acme.com',
    phone: '+1-555-0101',
    businessId: 1,
    branchId: 1,
    department: 'Information Technology',
    designation: 'Software Engineer',
    status: 'active',
    dateOfJoining: '2023-01-15',
    shift: 'morning',
  },
  {
    id: 2,
    firstName: 'Jane',
    lastName: 'Smith',
    name: 'Jane Smith',
    code: 'EMP002',
    email: 'jane@globex.com',
    phone: '+1-555-0102',
    businessId: 2,
    branchId: 3,
    department: 'Human Resources',
    designation: 'HR Manager',
    status: 'active',
    dateOfJoining: '2022-06-20',
    shift: 'morning',
  },
];

const Employee = () => {
  const [employees, setEmployees] = useState(initialEmployees);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState('all');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusConfirmDialogOpen, setStatusConfirmDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [viewTabValue, setViewTabValue] = useState(0);

  const handleChangePage = (_, newPage) => setPage(newPage);
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredBranches = selectedBusiness === 'all'
    ? branchOptions
    : branchOptions.filter(b => b.businessId.toString() === selectedBusiness);

  const filteredEmployees = employees.filter(emp => {
    const matchesBusiness = selectedBusiness === 'all' || emp.businessId.toString() === selectedBusiness;
    const matchesBranch = selectedBranch === 'all' || emp.branchId.toString() === selectedBranch;
    const matchesSearch =
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesBusiness && matchesBranch && matchesSearch;
  });

  const displayData = filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleView = (employee) => {
    setSelectedEmployee(employee);
    setViewDialogOpen(true);
    setViewTabValue(0);
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setEditDialogOpen(true);
  };

  const handleAddEmployee = (employeeData) => {
    const newEmp = {
      id: employees.length + 1,
      ...employeeData,
    };
    setEmployees((prev) => [...prev, newEmp]);
  };

  const handleUpdateEmployee = (updatedEmployee) => {
    setEmployees((prev) =>
      prev.map((emp) => (emp.id === updatedEmployee.id ? updatedEmployee : emp))
    );
    setEditDialogOpen(false);
  };

  const handleStatusToggle = (employee) => {
    setSelectedEmployee(employee);
    setStatusConfirmDialogOpen(true);
  };

  const confirmStatusToggle = () => {
    setEmployees(prev =>
      prev.map(item =>
        item.id === selectedEmployee.id
          ? { ...item, status: selectedEmployee.status === 'active' ? 'inactive' : 'active' }
          : item
      )
    );
    setStatusConfirmDialogOpen(false);
    setSelectedEmployee(null);
  };

  const getAvatarInitials = (name) => {
    const nameParts = name.trim().split(' ');
    if (nameParts.length === 1) {
      return name.charAt(0).toUpperCase() + (name.charAt(1) || '').toUpperCase();
    }
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Employee Directory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and view all employees
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              fontWeight: 600,
              px: 3,
              py: 1.2,
              textTransform: 'none',
              fontSize: '15px',
              boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
              borderRadius: 2
            }}
          >
            Add Employee
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Employee Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Company</InputLabel>
                <Select
                  value={selectedBusiness}
                  onChange={(e) => {
                    setSelectedBusiness(e.target.value);
                    setSelectedBranch('all');
                  }}
                  label="Company"
                >
                  <MenuItem value="all">All Companies</MenuItem>
                  {businessOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id.toString()}>{opt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  label="Branch"
                >
                  <MenuItem value="all">All Branches</MenuItem>
                  {filteredBranches.map(opt => (
                    <MenuItem key={opt.id} value={opt.id.toString()}>{opt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, code, or email..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Employee Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Employee List
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Employee</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Code</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Email</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Company</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Branch</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Shift</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }} align="center">Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.length > 0 ? (
                  displayData.map((emp) => {
                    const business = businessOptions.find(b => b.id === emp.businessId);
                    const branch = branchOptions.find(b => b.id === emp.branchId);
                    
                    return (
                      <TableRow
                        key={emp.id}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                          '&:hover': { backgroundColor: '#f0f7ff !important' }
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar
                              sx={{
                                bgcolor: getAvatarColor(emp.name),
                                width: 40,
                                height: 40,
                                fontSize: '0.875rem',
                                fontWeight: 700
                              }}
                            >
                              {getAvatarInitials(emp.name)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '14px' }}>
                                {emp.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {emp.designation}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{emp.code}</TableCell>
                        <TableCell>{emp.email}</TableCell>
                        <TableCell>{business?.name || '-'}</TableCell>
                        <TableCell>{branch?.name || '-'}</TableCell>
                        <TableCell>
                          {emp.shift ? emp.shift.charAt(0).toUpperCase() + emp.shift.slice(1) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={emp.status === 'active'}
                                onChange={() => handleStatusToggle(emp)}
                                color="success"
                                size="medium"
                              />
                            }
                            label={
                              <Chip
                                label={emp.status.charAt(0).toUpperCase() + emp.status.slice(1)}
                                size="small"
                                color={emp.status === 'active' ? 'success' : 'default'}
                                sx={{ fontWeight: 600 }}
                              />
                            }
                            sx={{ m: 0 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={0.5} justifyContent="center">
                            <Tooltip title="View Employee">
                              <IconButton
                                color="info"
                                size="small"
                                onClick={() => handleView(emp)}
                                sx={{ border: '1px solid #e0e0e0' }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Employee">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleEdit(emp)}
                                sx={{ border: '1px solid #e0e0e0' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No employees found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {filteredEmployees.length > 0 && (
            <TablePagination
              
              component="div"
              count={filteredEmployees.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Add Employee Dialog */}
      <AddEmployee
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAddEmployee={handleAddEmployee}
        businessOptions={businessOptions}
        branchOptions={branchOptions}
        departmentOptions={departmentOptions}
        designationOptions={designationOptions}
      />

      {/* View Employee Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          py: 2.5,
          px: 3
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            Employee Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {selectedEmployee && (
            <Box>
              <Tabs value={viewTabValue} onChange={(e, newValue) => setViewTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tab label="Personal" icon={<PersonIcon />} iconPosition="start" />
                <Tab label="Company" icon={<BusinessIcon />} iconPosition="start" />
                <Tab label="Bank" icon={<CreditCardIcon />} iconPosition="start" />
                <Tab label="Documents" icon={<DocumentIcon />} iconPosition="start" />
              </Tabs>

              {/* Personal Tab */}
              {viewTabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} display="flex" justifyContent="center" mb={2}>
                    <Avatar
                      sx={{
                        bgcolor: getAvatarColor(selectedEmployee.name),
                        width: 100,
                        height: 100,
                        fontSize: '2.5rem',
                        fontWeight: 700
                      }}
                    >
                      {getAvatarInitials(selectedEmployee.name)}
                    </Avatar>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      First Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee.firstName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Last Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee.lastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Phone
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee.phone}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {/* Company Tab */}
              {viewTabValue === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Employee Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee.code}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Date of Joining
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee.dateOfJoining}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Company
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {businessOptions.find(b => b.id === selectedEmployee.businessId)?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Branch
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {branchOptions.find(b => b.id === selectedEmployee.branchId)?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Department
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee.department}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Designation
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedEmployee.designation}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {/* Bank Tab */}
              {viewTabValue === 2 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  Bank details not available
                </Typography>
              )}

              {/* Documents Tab */}
              {viewTabValue === 3 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  Documents not available
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={() => setViewDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Employee Dialog */}
      <EditEmployee
        isOpen={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onUpdateEmployee={handleUpdateEmployee}
        selectedEmployee={selectedEmployee}
        businessOptions={businessOptions}
        branchOptions={branchOptions}
        departmentOptions={departmentOptions}
        designationOptions={designationOptions}
      />

      {/* Status Toggle Confirmation Dialog */}
      <Dialog 
        open={statusConfirmDialogOpen} 
        onClose={() => setStatusConfirmDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3,
          px: 3
        }}>
          <WarningIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {selectedEmployee?.status === 'active' ? 'Deactivate Employee' : 'Activate Employee'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to {selectedEmployee?.status === 'active' ? 'deactivate' : 'activate'} the employee <strong>"{selectedEmployee?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedEmployee?.status === 'active' 
              ? 'Deactivating this employee will prevent them from accessing the system.'
              : 'Activating this employee will restore their access to the system.'
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button 
            onClick={() => setStatusConfirmDialogOpen(false)}
            variant="outlined"
            sx={{ 
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmStatusToggle}
            variant="contained"
            color={selectedEmployee?.status === 'active' ? 'warning' : 'success'}
            startIcon={selectedEmployee?.status === 'active' ? <InactiveIcon /> : <ActiveIcon />}
            sx={{ 
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            {selectedEmployee?.status === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Employee;

